import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  AutoComplete,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { AlertBanner } from '../../../AlertBanner';
import { SignInButton } from '../../../SignInButton';
import { UserContext } from '../../../UserContext';
import {
  BroadcastMessage,
  BroadcastMessageCreate,
  Client,
} from '../../../types/broadcast';
import '../../../styles/global.css';

const { TextArea } = Input;

interface BroadcastMessageFormData {
  title: string;
  target_date: Dayjs;
  client_ids: number[];
  main_message_template: string;
  fast_follow_message_template: string;
}

export function BroadcastMessagesPage({
  apiUrl,
}: {
  apiUrl: string;
}): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [messages, setMessages] = useState<BroadcastMessage[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] =
    useState<BroadcastMessage | null>(null);
  const [form] = Form.useForm<BroadcastMessageFormData>();
  const [selectedClients, setSelectedClients] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [clientSearchText, setClientSearchText] = useState('');
  const user = useContext(UserContext);

  const fetchBroadcastMessages = async () => {
    if (!user) {
      return;
    }
    try {
      setLoading(true);
      setError(undefined);
      const response = await fetch(
        `${apiUrl}/api/daily-sync-broadcast-messages?include_past_broadcasts=true`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Error fetching broadcast messages: ${await response.text()}`,
        );
      }
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      setError(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    if (!user) {
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/clients`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  useEffect(() => {
    void fetchBroadcastMessages();
    void fetchClients();
  }, [user?.accessToken, apiUrl]);

  const handleCreateNew = () => {
    setEditingMessage(null);
    setSelectedClients([]);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (message: BroadcastMessage) => {
    setEditingMessage(message);
    const clientNames = message.client_ids.length === 0
      ? []
      : message.client_ids.map(id => {
          const client = clients.find(c => c.clientId === id);
          return {
            id,
            name: client?.friendlyName || `Client ${id}`,
          };
        });
    setSelectedClients(clientNames);
    form.setFieldsValue({
      title: message.title || '',
      target_date: dayjs(message.target_date),
      client_ids: message.client_ids,
      main_message_template: message.main_message_template || '',
      fast_follow_message_template:
        message.fast_follow_message_template || '',
    });
    setIsModalOpen(true);
  };

  const handleDeleteClient = (clientId: number) => {
    const newSelectedClients = selectedClients.filter(c => c.id !== clientId);
    setSelectedClients(newSelectedClients);
    form.setFieldsValue({
      client_ids: newSelectedClients.map(c => c.id),
    });
  };

  const handleAddClient = (value: string) => {
    if (!value.trim()) {
      return;
    }

    // Try to parse as client ID first
    const clientId = parseInt(value.trim(), 10);
    if (!isNaN(clientId)) {
      const client = clients.find(c => c.clientId === clientId);
      if (client && !selectedClients.find(c => c.id === clientId)) {
        const newClient = {
          id: clientId,
          name: client.friendlyName,
        };
        const newSelectedClients = [...selectedClients, newClient];
        setSelectedClients(newSelectedClients);
        form.setFieldsValue({
          client_ids: newSelectedClients.map(c => c.id),
        });
        setClientSearchText('');
        return;
      } else if (!client) {
        // Client ID not found, but add it anyway
        if (!selectedClients.find(c => c.id === clientId)) {
          const newClient = {
            id: clientId,
            name: `Client ${clientId}`,
          };
          setSelectedClients(prev => [...prev, newClient]);
          form.setFieldsValue({
            client_ids: [...selectedClients.map(c => c.id), clientId],
          });
          setClientSearchText('');
          return;
        }
      }
    }

    // Try to find by name
    const matchingClients = clients.filter(
      c =>
        c.friendlyName.toLowerCase().includes(value.toLowerCase()) &&
        !selectedClients.find(sc => sc.id === c.clientId),
    );
    if (matchingClients.length === 1) {
      const client = matchingClients[0];
      const newClient = {
        id: client.clientId,
        name: client.friendlyName,
      };
      const newSelectedClients = [...selectedClients, newClient];
      setSelectedClients(newSelectedClients);
      form.setFieldsValue({
        client_ids: newSelectedClients.map(c => c.id),
      });
      setClientSearchText('');
    } else if (matchingClients.length > 1) {
      message.info(
        `Multiple clients found. Please be more specific or use client ID.`,
      );
    }
  };

  const handleDelete = async () => {
    if (!user || !editingMessage) {
      return;
    }

    try {
      setError(undefined);
      const response = await fetch(
        `${apiUrl}/api/daily-sync-broadcast-messages/${editingMessage.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to delete broadcast message: ${await response.text()}`,
        );
      }

      message.success('Broadcast message deleted successfully');
      setIsModalOpen(false);
      setEditingMessage(null);
      setSelectedClients([]);
      form.resetFields();
      void fetchBroadcastMessages();
    } catch (error) {
      setError(`Error deleting message: ${error}`);
    }
  };

  const handleSubmit = async (values: BroadcastMessageFormData) => {
    if (!user) {
      return;
    }

    try {
      setError(undefined);
      // Empty array means "all clients"
      const clientIds = selectedClients.map(c => c.id);
      const payload: BroadcastMessageCreate = {
        title: values.title,
        target_date: values.target_date.format('YYYY-MM-DD'),
        client_ids: clientIds,
        main_message_template: values.main_message_template || null,
        fast_follow_message_template:
          values.fast_follow_message_template || null,
      };

      if (
        !payload.main_message_template &&
        !payload.fast_follow_message_template
      ) {
        message.error(
          'At least one of main message or follow on message must be provided',
        );
        return;
      }

      const url = editingMessage
        ? `${apiUrl}/api/daily-sync-broadcast-messages/${editingMessage.id}`
        : `${apiUrl}/api/daily-sync-broadcast-messages`;
      const method = editingMessage ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${editingMessage ? 'update' : 'create'} broadcast message: ${await response.text()}`,
        );
      }

      message.success(
        `Broadcast message ${editingMessage ? 'updated' : 'created'} successfully`,
      );
      setIsModalOpen(false);
      setEditingMessage(null);
      setSelectedClients([]);
      form.resetFields();
      void fetchBroadcastMessages();
    } catch (error) {
      setError(`Error ${editingMessage ? 'updating' : 'creating'} message: ${error}`);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MM/DD/YY');
  };

  const getClientDisplay = (clientIds: number[]) => {
    if (clientIds.length === 0) {
      return 'all';
    }
    return clientIds.join(', ');
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string | undefined) => text || 'Untitled',
    },
    {
      title: 'Client ID',
      key: 'client_ids',
      render: (_: unknown, record: BroadcastMessage) =>
        getClientDisplay(record.client_ids),
    },
    {
      title: 'Date',
      dataIndex: 'target_date',
      key: 'target_date',
      render: (date: string) => formatDate(date),
      sorter: (a: BroadcastMessage, b: BroadcastMessage) =>
        dayjs(a.target_date).unix() - dayjs(b.target_date).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: BroadcastMessage) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          type="link"
        >
          Edit
        </Button>
      ),
    },
  ];

  const filteredClients = clients.filter(
    client =>
      !selectedClients.find(sc => sc.id === client.clientId) &&
      (client.friendlyName
        .toLowerCase()
        .includes(clientSearchText.toLowerCase()) ||
        client.clientId.toString().includes(clientSearchText)),
  );

  return (
    <>
      <UnauthenticatedTemplate>
        <SignInButton loginType="popup" />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AlertBanner error={error} setError={setError} />

        <Typography.Title
          level={4}
          style={{
            margin: '0 0 24px 0',
            fontSize: '22px',
            color: '#333',
          }}
        >
          Broadcast Messages
        </Typography.Title>

        <Card
          bordered={false}
          style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '24px',
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Button onClick={handleCreateNew} type="primary">
              Create NEW
            </Button>
          </div>

          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 0',
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={messages}
              rowKey="id"
              style={{ marginBottom: '24px' }}
            />
          )}
        </Card>

        <Modal
          footer={null}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingMessage(null);
            setSelectedClients([]);
            form.resetFields();
          }}
          open={isModalOpen}
          title={editingMessage ? 'Edit Broadcast Message' : 'Create Broadcast Message'}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '24px' }}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input placeholder="Enter message title" />
            </Form.Item>

            <Form.Item
              label="Main Message"
              name="main_message_template"
              rules={[
                {
                  validator: (_rule, value) => {
                    const followOn =
                      form.getFieldValue('fast_follow_message_template');
                    if (!value && !followOn) {
                      return Promise.reject(
                        new Error(
                          'At least one of main message or follow on message must be provided',
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea
                placeholder="Enter main message template"
                rows={4}
              />
            </Form.Item>

            <Form.Item
              label="Follow On Message"
              name="fast_follow_message_template"
              rules={[
                {
                  validator: (_rule, value) => {
                    const main =
                      form.getFieldValue('main_message_template');
                    if (!value && !main) {
                      return Promise.reject(
                        new Error(
                          'At least one of main message or follow on message must be provided',
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea
                placeholder="Enter follow on message template"
                rows={4}
              />
            </Form.Item>

            <Form.Item
              label="Date"
              name="target_date"
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item label="Client List">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space.Compact style={{ width: '100%' }}>
                  <AutoComplete
                    onSelect={(value: string) => {
                      handleAddClient(value);
                    }}
                    onSearch={setClientSearchText}
                    options={filteredClients.slice(0, 20).map(client => ({
                      label: `${client.friendlyName} (${client.clientId})`,
                      value: client.clientId.toString(),
                    }))}
                    placeholder="Search by client name or enter client ID"
                    style={{ flex: 1 }}
                    value={clientSearchText}
                    filterOption={(inputValue, option) => {
                      if (!option) return false;
                      return (
                        option.label
                          ?.toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        option.value?.includes(inputValue)
                      );
                    }}
                  />
                  <Button
                    onClick={() => {
                      if (clientSearchText) {
                        handleAddClient(clientSearchText);
                      }
                    }}
                    type="primary"
                  >
                    Add
                  </Button>
                </Space.Compact>

                <div style={{ marginTop: '12px', minHeight: '32px' }}>
                  {selectedClients.length === 0 ? (
                    <Tag color="blue">all</Tag>
                  ) : (
                    selectedClients.map(client => (
                      <Tag
                        closable
                        key={client.id}
                        onClose={() => handleDeleteClient(client.id)}
                        style={{ marginBottom: '8px' }}
                      >
                        {client.name} ({client.id})
                      </Tag>
                    ))
                  )}
                </div>
              </Space>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                  {editingMessage && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <Space>
                  <Button htmlType="submit" type="primary">
                    {editingMessage ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingMessage(null);
                      setSelectedClients([]);
                      form.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </AuthenticatedTemplate>
    </>
  );
}
